import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

// Toolkit
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { assessmentUpdate } from "sj-ui-toolkit/src/client";
import { FullScreenLoader } from "sj-ui-toolkit/src/components";
import { buildRoute } from "sj-ui-toolkit/src/constants/routes";
import { useTrackTime } from "sj-ui-toolkit/src/hooks/useTrackTime";
import { exitScormLesson, setScormPassed } from "sj-ui-toolkit/src/utils/rxd";

// Context providers
import { useLearnContext } from "../../context/LearnContext";

// Hooks
import { useLessonLoader } from "../../hooks/useLessonLoader";
import { usePathCompletion } from "../../hooks/usePathCompletion";
import { useToast } from "sj-ui-toolkit/src/hooks";

// Sub-components
import AppLabLesson from "./AppLabLesson/AppLabLesson.jsx";
import LessonCoverPage from "./LessonCoverPage.jsx";
import { PathCompletionPage } from "./PathCompletion/PathCompletionPage.jsx";
import SandboxLesson from "./SandboxLesson";
import VideoLesson from "./VideoLesson";

// Main component
export default function LessonPage() {
  const { lessonKey } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isScorm, isGuest } = useLearnContext();
  const { showToast } = useToast();

  const mainRef = useRef(null);

  // Use the lesson loader hook
  const { lessonDetails, setLessonDetails } = useLessonLoader(lessonKey);
  const hasNextLesson =
    searchParams.get("inPath") === "true" && !!lessonDetails?.nextLesson?.lessonKey;

  // Register this page's breadcrumb — trail is auto-accumulated by the provider
  useBreadcrumb(
    lessonDetails ? { label: lessonDetails.title, route: `/lesson/${lessonKey}` } : null
  );

  // Track time spent on the lesson
  useTrackTime({ lessonId: lessonDetails?.id });

  // Subscribe to PathCompletedChannel — silently stores data until lesson finishes
  const { isPathCompleted, completedPath } = usePathCompletion();

  // Track whether to show the path completion screen (after survey, before navigation)
  const [showPathCompletion, setShowPathCompletion] = useState(false);

  // Update page title for accessibility
  useEffect(() => {
    const previousPageTitle = document.title;

    if (lessonDetails?.title) {
      document.title = lessonDetails.title;
    }

    return () => {
      document.title = previousPageTitle;
    };
  }, [lessonDetails?.title]);

  // Reposition screen reader focus on view transition
  useEffect(() => {
    mainRef.current?.focus();
  }, [lessonDetails?.showBeforeSurvey, lessonDetails?.showAfterSurvey, showPathCompletion]);

  // Define handlers for user actions
  const submitKnowledgeRating = async (rating, isExiting = false) => {
    const isAfterPage = lessonDetails.showAfterSurvey;

    if (rating) {
      const params = {};
      if (isAfterPage) {
        params.after_rating = parseInt(rating, 10);
      } else {
        params.before_rating = parseInt(rating, 10);
      }

      try {
        await assessmentUpdate({
          id: lessonDetails.assessment.id,
          paramsToUpdate: params,
          useToast: false,
        });
      } catch (error) {
        console.error("Error submitting knowledge rating:", error);
        showToast({
          severity: "error",
          summary: "Submission failed",
          detail: "There was an issue submitting your feedback. Please try again.",
          life: 5000,
        });
      }
    }

    // Proceed based on whether it was before or after survey
    if (isAfterPage) {
      // If a path was completed, show the path completion screen before navigating
      if (isPathCompleted && completedPath) {
        setShowPathCompletion(true);
        return;
      }

      if (isExiting) {
        navigate("/");
        return;
      }
      // Navigate to the next lesson if we're in a path context and nextLesson exists
      if (hasNextLesson) {
        navigate(
          buildRoute(
            "Learn.routes.lesson.route",
            {
              lessonKey: lessonDetails.nextLesson?.lessonKey,
            },
            { inPath: true }
          )
        );
      } else {
        navigate("/");
      }
    } else {
      // Hide the before survey and start the lesson
      setLessonDetails({ ...lessonDetails, showBeforeSurvey: false });
    }
  };

  const onLessonFinish = () => {
    if (!isGuest && !isScorm) {
      setLessonDetails({ ...lessonDetails, showAfterSurvey: true });
    } else {
      // Set SCORM as passed and exit the lesson for guest users and SCORM content since they won't see the after lesson survey
      setScormPassed();
      exitScormLesson();
    }
  };

  // Return the full screen loader until we have lesson details
  if (!lessonDetails) {
    return (
      <div className="h-full">
        <FullScreenLoader />
      </div>
    );
  }

  // Show path completion screen after the survey if a path was completed
  if (showPathCompletion && completedPath) {
    return (
      <div ref={mainRef} tabIndex={-1} className="outline-none h-full">
        <PathCompletionPage completedPath={completedPath} />
      </div>
    );
  }

  // Render the After Lesson Feedback page if needed
  if (lessonDetails.showAfterSurvey) {
    return (
      <div ref={mainRef} tabIndex={-1} className="outline-none h-full">
        <LessonCoverPage
          headerText="Lesson complete!"
          description="Let us know how you did."
          lessonType={lessonDetails.type}
          title={lessonDetails.title}
          level={lessonDetails.level}
          duration={lessonDetails.duration}
          isAfterLessonSurvey={true}
          onSubmit={submitKnowledgeRating}
          hasNextLesson={hasNextLesson}
        />
      </div>
    );
  }

  // Render the Getting Started screen/survey if needed
  if (lessonDetails.showBeforeSurvey && !isGuest && !isScorm) {
    return (
      <div ref={mainRef} tabIndex={-1} className="outline-none h-full">
        <LessonCoverPage
          headerText="Let's get started."
          description={lessonDetails.description}
          lessonType={lessonDetails.type}
          title={lessonDetails.title}
          difficulty={lessonDetails.difficulty}
          duration={lessonDetails.duration}
          isAfterLessonSurvey={false}
          onSubmit={submitKnowledgeRating}
        />
      </div>
    );
  }

  // Render the appropriate lesson type
  if (lessonDetails.isBreakFix) {
    if (lessonDetails.lessonType === "AppLab") {
      return (
        <div ref={mainRef} tabIndex={-1} className="outline-none h-full">
          <AppLabLesson lessonDetails={lessonDetails} onLessonFinish={onLessonFinish} />
        </div>
      );
    } else {
      return (
        <div ref={mainRef} tabIndex={-1} className="outline-none h-full">
          <SandboxLesson lessonDetails={lessonDetails} onLessonFinish={onLessonFinish} />
        </div>
      );
    }
  } else {
    return (
      <div ref={mainRef} tabIndex={-1} className="outline-none h-full">
        <VideoLesson lessonDetails={lessonDetails} onLessonFinish={onLessonFinish} />
      </div>
    );
  }
}
